import { Box, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { formatPhone } from '@functions/format-phone';
import { leadChannel, leadChannelColor } from '@layouts/pages/CRM/lead/lead-channel';
import { LeadResponse } from '../../services/responses/lead.response';
import { getCrmColors } from '../../crm-theme';
import { formatCrmShortDateTime, localDayKey } from '../../formatters/crm-datetime';
import { formatRoundedBrl, leadAmount } from '../../formatters/crm-lead-amount';
import { sourceLabel } from '../../formatters/lead-source-label';
import { matchesScheduleFilter } from '../../filters/crm-schedule-filter';
import { CrmLeadStatusChip } from '../shared/crm-lead-status-chip';
import { CrmLeadOwnerAvatar } from '../shared/crm-lead-owner-avatar';
import {
    OWNER_CELL_SX, ROW_SX, TABLE_HEAD_SX, VALUE_CELL_SX, cellActionSx, leadValueSx,
} from './crm-lead-list.styles';

export type SortDirection = 'asc' | 'desc';

/// Abaixo dessa largura saem as colunas Origem e Criado em.
export const NARROW_WIDTH = 1450;

/// Data e valor começam do maior, texto de A a Z e compromisso do mais cedo (o que vence importa).
const DESC_FIRST = ['Value', 'LastMessageAt', 'CreatedAt'];

export const firstSortDirection = (field: string): SortDirection =>
    (DESC_FIRST.includes(field) ? 'desc' : 'asc');

interface Props {
    leads: LeadResponse[];
    loaded: boolean;
    /// Tabela estreita: sem Origem e Criado em.
    compact: boolean;
    stageNames: Map<string, string>;
    /// Campo de LeadSorting no backend.
    orderBy: string;
    orderDirection: SortDirection;
    onSort: (field: string) => void;
    onOpen: (lead: LeadResponse) => void;
    /// Ausentes quando o quadro está arquivado ou não há opções.
    onOpenStatusMenu?: (lead: LeadResponse, anchor: HTMLElement) => void;
    onOpenAssignMenu?: (lead: LeadResponse, anchor: HTMLElement) => void;
}

export const CrmLeadListTable = ({
    leads, loaded, compact, stageNames, orderBy, orderDirection, onSort, onOpen, onOpenStatusMenu, onOpenAssignMenu,
}: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const navigate = useNavigate();

    const columnCount = compact ? 7 : 9;
    const directionOf = (field: string) => (orderBy === field ? orderDirection : firstSortDirection(field));

    /// Célula com ação própria: o clique não chega à linha, que abre o lead.
    const ownAction = (run: (anchor: HTMLElement) => void) => (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        run(e.currentTarget);
    };

    const empty = <Typography variant="caption" sx={{ color: cc.textMuted }}>—</Typography>;

    return (
        <Table size="medium">
            <TableHead sx={TABLE_HEAD_SX}>
                <TableRow>
                    <TableCell width="17%">
                        <TableSortLabel active={orderBy === 'ContactName'} direction={directionOf('ContactName')} onClick={() => onSort('ContactName')}>
                            {t('crmLista.lead')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell width="12%">
                        <TableSortLabel active={orderBy === 'Stage'} direction={directionOf('Stage')} onClick={() => onSort('Stage')}>
                            {t('crmLista.etapa')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell width="1%">
                        <TableSortLabel active={orderBy === 'Status'} direction={directionOf('Status')} onClick={() => onSort('Status')}>
                            {t('crmLista.status')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell width="8%" align="right" sx={VALUE_CELL_SX}>
                        <TableSortLabel active={orderBy === 'Value'} direction={directionOf('Value')} onClick={() => onSort('Value')}>
                            {t('crmLista.valor')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell width="4%" sx={OWNER_CELL_SX}>
                        <TableSortLabel active={orderBy === 'AssignedUser'} direction={directionOf('AssignedUser')} onClick={() => onSort('AssignedUser')}>
                            {t('crmLista.responsavel')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell width="10%">
                        <TableSortLabel active={orderBy === 'NextScheduledAt'} direction={directionOf('NextScheduledAt')} onClick={() => onSort('NextScheduledAt')}>
                            {t('crmLista.proximaAcao')}
                        </TableSortLabel>
                    </TableCell>
                    <TableCell width="16%">
                        <TableSortLabel active={orderBy === 'LastMessageAt'} direction={directionOf('LastMessageAt')} onClick={() => onSort('LastMessageAt')}>
                            {t('crmLista.ultimaMensagem')}
                        </TableSortLabel>
                    </TableCell>
                    {!compact && (
                        <TableCell width="6%">
                            <TableSortLabel active={orderBy === 'Source'} direction={directionOf('Source')} onClick={() => onSort('Source')}>
                                {t('crmLista.origem')}
                            </TableSortLabel>
                        </TableCell>
                    )}
                    {!compact && (
                        <TableCell width="6%">
                            <TableSortLabel active={orderBy === 'CreatedAt'} direction={directionOf('CreatedAt')} onClick={() => onSort('CreatedAt')}>
                                {t('crmLista.criadoEm')}
                            </TableSortLabel>
                        </TableCell>
                    )}
                </TableRow>
            </TableHead>

            <TableBody>
                {!loaded && Array.from({ length: 5 }).map((_, row) => (
                    <TableRow key={row}>
                        {Array.from({ length: columnCount }).map((__, cell) => (
                            <TableCell key={cell}><Skeleton height={28} /></TableCell>
                        ))}
                    </TableRow>
                ))}

                {loaded && leads.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={columnCount} align="center">{t('crmLista.nenhumLead')}</TableCell>
                    </TableRow>
                )}

                {loaded && leads.map((lead) => (
                    <TableRow key={lead.id} hover onClick={() => onOpen(lead)} sx={ROW_SX}>
                        <TableCell>
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ color: cc.textPrimary, maxWidth: 230 }}>
                                {lead.contactName}
                            </Typography>
                            {lead.contactPhone && (
                                <Typography variant="caption" sx={{ color: cc.textSecondary }}>
                                    {formatPhone(lead.contactPhone)}
                                </Typography>
                            )}
                        </TableCell>

                        <TableCell>
                            <Tooltip title={stageNames.get(lead.columnId) ?? ''} placement="top-start">
                                <Typography variant="body2" noWrap sx={{ color: cc.textPrimary, maxWidth: 200 }}>
                                    {stageNames.get(lead.columnId) ?? '—'}
                                </Typography>
                            </Tooltip>
                        </TableCell>

                        <TableCell>
                            <CrmLeadStatusChip
                                lead={lead}
                                size="medium"
                                onOpenMenu={onOpenStatusMenu && ((anchor) => onOpenStatusMenu(lead, anchor))}
                            />
                        </TableCell>

                        <TableCell align="right" sx={VALUE_CELL_SX}>
                            {leadAmount(lead) == null ? empty : (
                                <Typography variant="body2" sx={leadValueSx(cc, lead.closedValue != null)}>
                                    {formatRoundedBrl(leadAmount(lead))}
                                </Typography>
                            )}
                        </TableCell>

                        <TableCell sx={OWNER_CELL_SX}>
                            {!lead.assignedUserName && !onOpenAssignMenu ? empty : (
                                <Box
                                    onClick={onOpenAssignMenu && ownAction((anchor) => onOpenAssignMenu(lead, anchor))}
                                    sx={cellActionSx(cc, Boolean(onOpenAssignMenu), 1)}
                                >
                                    <CrmLeadOwnerAvatar lead={lead} size="medium" />
                                    {lead.assignedUserName && (
                                        <Typography variant="body2" noWrap sx={{ color: cc.textPrimary, maxWidth: 160 }}>
                                            {lead.assignedUserName}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </TableCell>

                        {/* Leva à agenda no dia do compromisso. */}
                        <TableCell>
                            {!lead.nextScheduledAt ? empty : (
                                <Box
                                    onClick={ownAction(() => navigate(`/crm/agenda?dia=${localDayKey(lead.nextScheduledAt)}`))}
                                    sx={cellActionSx(
                                        cc,
                                        true,
                                        1,
                                        matchesScheduleFilter(lead.nextScheduledAt, 'late') ? cc.scheduleLate : cc.scheduleSoon
                                    )}
                                >
                                    <Typography variant="body2" fontWeight={700} sx={{ color: 'inherit' }}>
                                        {formatCrmShortDateTime(lead.nextScheduledAt)}
                                    </Typography>
                                </Box>
                            )}
                        </TableCell>

                        {/* Com conversa, leva ao Multicanal; sem ela, o texto é só texto. */}
                        <TableCell>
                            {!lead.lastMessage ? empty : (
                                <Box
                                    onClick={lead.chatwootConversationId
                                        ? ownAction(() => navigate(`/multicanal?conversa=${lead.chatwootConversationId}`))
                                        : undefined}
                                    sx={cellActionSx(cc, Boolean(lead.chatwootConversationId), 0.75)}
                                >
                                    <Box
                                        component={leadChannel(lead.source)?.Icon ?? ChatIcon}
                                        sx={{ fontSize: 17, color: leadChannelColor(lead.source, cc), flexShrink: 0 }}
                                    />
                                    <Tooltip title={lead.lastMessage} placement="top-start">
                                        <Typography variant="body2" noWrap sx={{ color: cc.textSecondary, maxWidth: 240 }}>
                                            {lead.lastMessage}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            )}
                        </TableCell>

                        {!compact && <TableCell>{sourceLabel(t, lead.source, lead.sourceDetail)}</TableCell>}
                        {!compact && (
                            <TableCell sx={{ color: cc.textSecondary }}>{formatCrmShortDateTime(lead.createdAt)}</TableCell>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};
